import React, { useMemo } from 'react'
import useQuery from 'src/hooks/useQuery'
import useHasPermission from 'src/hooks/useHasPermission'
import { Permissions } from '@wingwork/common/src/constants'
import ReviewForm from 'src/components/ReviewForm'
import WorkOrderCompletedModal from './WorkOrderCompletedModal'
import {
  GET_WORK_ORDER_CUSTOM_TASK_LIST,
  GET_WORK_ORDER_TASK_LIST,
  GET_WORK_ORDER_TASK_LIST_PAGINATED,
} from 'src/pages/WorkOrderCompliancePageV2/queries'
import Loading from 'src/components/Loading'
import CreateLogbookEntryModal from 'src/components/CreateLogbookEntryModal'
import ViewLogbookEntryModal from 'src/components/ViewLogbookEntryModal/ViewLogbookEntryModal'
import {
  MassagedComplianceActivity,
  massageTasks,
} from 'src/components/ReviewForm/helper'
import { InternalWorkItem } from 'types/graphql'
import useFetchAll from 'src/hooks/useFetchAll'

interface WorkOrderCompletedContentProps {
  id: string
  aircraftId: string
  ledgerId: string
  onCancel?: () => void
  createdLogbook: boolean
}

const PAGE_SIZE = 10
const WorkOrderCompletedContent: React.FC<WorkOrderCompletedContentProps> = ({
  id,
  aircraftId,
  ledgerId,
  onCancel,
  createdLogbook,
}) => {
  const canCompleteWorkOrder = useHasPermission(
    Permissions.workOrder.complete,
    aircraftId
  )

  const {
    data: taskData,
    loading: firstPageLoading,
    fetchMore,
    error: taskDataError,
  } = useQuery(GET_WORK_ORDER_TASK_LIST_PAGINATED, {
    variables: { id, limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
  })

  const { hasAllData } = useFetchAll({
    fetchMore,
    strategy: 'offset',
    params: {
      data: taskData,
      loading: firstPageLoading,
      limit: PAGE_SIZE,
      path: 'internalWorkItemsPaginated',
    },
    autoFetch: true,
  })

  const {
    data: customTaskData,
    loading: customTaskDataLoading,
    error: customTaskDataError,
  } = useQuery<
    { customInternalWorkItems?: InternalWorkItem[] },
    { id: string }
  >(GET_WORK_ORDER_CUSTOM_TASK_LIST, {
    variables: { id },
    fetchPolicy: 'cache-first',
  })

  const { workItems, customWorkItems } = useMemo(() => {
    return {
      workItems: taskData?.internalWorkItemsPaginated ?? [],
      customWorkItems: customTaskData?.customInternalWorkItems ?? [],
    }
  }, [taskData, customTaskData])

  const tasks: MassagedComplianceActivity[] = useMemo(() => {
    if (!workItems?.length) return []
    return [
      ...massageTasks(workItems, true, false),
      ...massageTasks(
        customTaskData?.customInternalWorkItems ?? [],
        true,
        true
      ),
    ]
  }, [workItems, customWorkItems])

  if (!hasAllData || customTaskDataLoading) {
    return <Loading />
  }

  return (
    <div className="flex h-full flex-col">
      <ReviewForm
        title="Information Summary"
        tasks={tasks}
        hasCorrectiveAction
        hasAccounting
        readonly
        aircraftId={aircraftId}
        mode="completed"
      />
      <WorkOrderCompletedModal workOrderId={id} ledgerId={ledgerId} />
      {createdLogbook ? (
        <ViewLogbookEntryModal ledgerId={ledgerId} workOrderId={id} />
      ) : (
        <CreateLogbookEntryModal propLedgerId={ledgerId} workOrderId={id} />
      )}
    </div>
  )
}

export default WorkOrderCompletedContent
